/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import {
  ErrorCodes,
  getAuthSnapshot,
  subscribeAuthGeneration,
} from '@adp-arch/bot-http';
import {
  fileMemoryApi,
  FileIndexStatus,
  type FileMemoryFile,
} from '@adp-arch/bot-api/filememory';

const POLL_INTERVAL_ACTIVE_MS = 3000;
const POLL_INTERVAL_IDLE_MS = 15000;
const RETRY_MAX_MS = 60000;
const RETRY_FACTOR = 2;
const RETRY_MAX_EXPONENT = 5;
const JITTER_MIN_RATIO = 0.5;

enum HttpStatus {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const classifyError = (error: unknown): 'auth' | 'stop' | 'retry' => {
  const { response, code, name } = (error ?? {}) as {
    response?: { status?: number };
    code?: string;
    name?: string;
  };
  const status = response?.status;
  if (
    status === HttpStatus.Unauthorized ||
    status === HttpStatus.Forbidden ||
    String(code) === String(ErrorCodes.NOT_LOGIN)
  ) {
    return 'auth';
  }
  if (
    code === 'ERR_CANCELED' ||
    name === 'AbortError' ||
    (status &&
      status >= HttpStatus.BadRequest &&
      status < HttpStatus.InternalServerError &&
      status !== HttpStatus.TooManyRequests)
  ) {
    return 'stop';
  }
  return 'retry';
};

const retryDelay = (failures: number) => {
  // Equal jitter, exponentially increasing to a hard 60s ceiling.
  const ceiling = Math.min(
    RETRY_MAX_MS,
    POLL_INTERVAL_ACTIVE_MS *
      RETRY_FACTOR ** Math.min(failures, RETRY_MAX_EXPONENT),
  );
  return ceiling * (JITTER_MIN_RATIO + Math.random() * JITTER_MIN_RATIO);
};

export interface FileIndexingState {
  indexing_status: number;
  indexing_error?: string;
}

const buildStatusMap = (files: FileMemoryFile[]) => {
  const nextMap = new Map<string, FileIndexingState>();
  let hasPending = false;
  for (const file of files) {
    hasPending ||=
      file.index_status === FileIndexStatus.Pending ||
      file.index_status === FileIndexStatus.Indexing;
    nextMap.set(file.file_key || file.id || file.file_name, {
      indexing_status: file.index_status,
      indexing_error: file.error_msg || undefined,
    });
  }
  return {
    nextMap,
    interval: hasPending ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_IDLE_MS,
  };
};

/**
 * Polls the filememory API for file indexing status.
 * Returns a map of stable file identity → indexing state.
 * Keeps polling for the active conversation so later uploads are observed.
 * Uses backoff polling (15s idle, 3s active) to reduce server load.
 */
export const useFileIndexingStatus = (
  conversationId: string | null,
): Map<string, FileIndexingState> => {
  const [statusMap, setStatusMap] = useState<Map<string, FileIndexingState>>(
    () => new Map(),
  );
  const auth = useSyncExternalStore(
    subscribeAuthGeneration,
    getAuthSnapshot,
    getAuthSnapshot,
  );
  const generationRef = useRef(0);
  // A conversation switch or focus must not revive an unauthorized poller.
  const terminalAuthGeneration = useRef<number>();

  useEffect(() => {
    const generation = ++generationRef.current;
    setStatusMap(new Map());
    if (
      !conversationId ||
      auth.authenticated === false ||
      terminalAuthGeneration.current === auth.generation
    ) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let inFlight = false;
    let stopped = false;
    let failures = 0;
    const isCurrent = () =>
      !stopped &&
      generation === generationRef.current &&
      auth.generation === getAuthSnapshot().generation;

    const fetchStatus = async () => {
      if (!isCurrent() || inFlight) {
        return;
      }
      clearTimeout(timer);
      inFlight = true;
      controller = new AbortController();
      let delay: number | undefined;
      try {
        const files = await fileMemoryApi.listFiles(
          conversationId,
          controller.signal,
        );
        if (!isCurrent()) {
          return;
        }
        const { nextMap, interval } = buildStatusMap(files);
        setStatusMap(nextMap);
        failures = 0;
        delay = interval;
      } catch (error) {
        if (!isCurrent() || controller.signal.aborted) {
          return;
        }
        const kind = classifyError(error);
        if (kind === 'auth') {
          terminalAuthGeneration.current = auth.generation;
        }
        if (kind !== 'retry') {
          stopped = true;
          return;
        }
        delay = retryDelay(++failures);
      } finally {
        inFlight = false;
        if (isCurrent() && delay !== undefined) {
          timer = setTimeout(fetchStatus, delay);
        }
      }
    };

    void fetchStatus();

    const handleFocus = () => {
      // Focus must not bypass retry backoff or overlap a pending request.
      if (failures === 0) {
        void fetchStatus();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopped = true;
      generationRef.current = generation + 1;
      controller?.abort();
      clearTimeout(timer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [conversationId, auth]);

  return statusMap;
};
